import {
  type KrpPlayer,
  type MatrixFov,
  MatrixFovType,
  type PanoMultires,
  type STImage,
  type STImageMarker,
  type STImageMarkerScene,
  type STMatrix,
  SandTableType,
  SceneType,
} from '@types';
import colorStringToHexAndAlpha from '@utils/colorStringToHexAndAlpha';
import getPanoUrl from '@utils/getPanoUrl';
import { spotTitleId } from '@utils/spotTitleId';
import { getMediaUrl, getSysUrl } from '@utils/urlMakers';
// @ts-ignore
import { KrpanoPlayer } from 'pano-sdk';
import { parseJsonString } from 'pano-utils';
import { viewDefault } from '../../view';
import fetchSandTableMatrices from '../actions/fetchSandTableMatrices';
import { MARKER_IMAGE_SIZE, MARKER_SIZE } from '../constants';

const EDITING_ID = 'st_image_marker_editing';
const RADAR_ID = 'st_image_marker_radar';
const EDITING_ZORDER = 300;

const getImageMarkerUrl = ({
  src,
  type,
  size,
}: { src: string; type: STImageMarker['icon']['type']; size: number }) => {
  if (type === 1) {
    return `${getMediaUrl(
      src
    )}?imageMogr2/auto-orient/thumbnail/${size}x${size}!`;
  }
  return `${getSysUrl(
    src
  )}?x-oss-process=image/auto-orient,1/resize,s_${size},m_lfit`;
};

class STPlayer {
  private player: KrpPlayer | null = null;

  private onRadarRotate?: (angle: number) => void;

  private onMarkerMoveEnd?: (params: {
    id: string;
    ath: number;
    atv: number;
  }) => void;

  private editingId?: string;

  private radarSize?: number;

  private northAngle = 0;

  private editingScale = 1;

  private loadScene = async (data: STMatrix | STImage) => {
    if (!this.player) return;
    if (data.type === SandTableType.image) {
      this.player.loadFlat({
        singleImageUrl: getMediaUrl(data.mediaUrl),
        view: {
          ...viewDefault,
          flatFovType: MatrixFovType.custom,
          maxPixelZoom: 2,
        },
      });
    } else if (data.type === SandTableType.matrix) {
      const result = await fetchSandTableMatrices(data.data);
      if (!result[0]) return;
      const { path, multires, cdnDomain, fov } = result[0];
      this.player.loadFlat({
        url: getPanoUrl({ cdnDomain, path }),
        multires: parseJsonString(multires) as PanoMultires,
        matrixFov: parseJsonString(fov) as MatrixFov,
        view: {
          ...viewDefault,
          flatFovType: MatrixFovType.custom,
          maxPixelZoom: 2,
        },
      });
    }
  };

  create = ({
    target,
    data,
    radarSize = 60,
    onReady,
    onRadarRotate,
    onMarkerClick,
    onMarkerMoveEnd,
  }: {
    target: string;
    data: STMatrix | STImage;
    radarSize?: number;
    onReady: () => void;
    onRadarRotate?: (angle: number) => void;
    onMarkerClick?: (id: string) => void;
    onMarkerMoveEnd?: (params: {
      id: string;
      ath: number;
      atv: number;
    }) => void;
  }) => {
    if (this.player) {
      this.player.removePlayer();
    }
    this.onRadarRotate = onRadarRotate;
    this.onMarkerMoveEnd = onMarkerMoveEnd;
    this.radarSize = radarSize;
    this.northAngle = data.angle ?? 0;
    this.player = new KrpanoPlayer() as KrpPlayer;
    this.player.createPlayer(
      target,
      {
        onHotspotMoveEnd: ({ id }) => {
          const spot = this.player?.getHotspotById(id);
          if (!spot) return;
          onMarkerMoveEnd?.({ id, ath: spot.ath, atv: spot.atv });
        },
        onHotspotClick: ({ id }) => {
          const targetId = this.player
            ?.getHotspotById(id)
            .getExtendData('onClickId');
          if (targetId) {
            onMarkerClick?.(targetId);
          }
        },
        onAnimationComplete: () => {
          onReady();
        },
      },
      () => {
        this.loadScene(data);
        this.player?.controlMode({ mouse: 'drag' });
      },
      {
        wmode: 'transparent',
      }
    );
  };

  remove = () => {
    this.player?.removePlayer();
    this.player = null;
  };

  getCenter = () => {
    const view = this.player?.getView();
    return {
      ath: view?.hlookat ?? 0,
      atv: view?.vlookat ?? 0,
      fov: view?.fov ?? 0,
    };
  };

  static iconParams = ({
    icon,
    isActive,
    markerSize,
  }: {
    icon: STImageMarkerScene['icon'];
    isActive?: boolean;
    markerSize?: number;
  }) => {
    const {
      scaleX,
      scaleY,
      fillColor,
      borderColor,
      activeFillColor,
      activeBorderColor,
    } = icon;
    const iconBGC = colorStringToHexAndAlpha(
      isActive ? activeFillColor : fillColor
    );
    const iconBC = colorStringToHexAndAlpha(
      isActive ? activeBorderColor : borderColor
    );
    return {
      width: markerSize || MARKER_SIZE * scaleX,
      height: markerSize || MARKER_SIZE * scaleY,
      roundedge: (markerSize || MARKER_SIZE * scaleX) / 2 + 2,
      fillColor: iconBGC.hex,
      fillAlpha: iconBGC.alpha,
      lineWidth: 2, // 描边粗细
      lineColor: iconBC.hex, // 描边颜色
      lineAlpha: iconBC.alpha, // 描边透明度
    };
  };

  static titleParams = (marker: STImageMarker) => {
    const { title, titleSetting, showTitle } = marker;
    const { font, background } = titleSetting;
    const fontC = colorStringToHexAndAlpha(font?.fontColor);
    const titleBGC = colorStringToHexAndAlpha(background?.bgColor);
    return {
      text: title,
      ox: titleSetting.ox,
      oy: titleSetting.oy,
      edge: titleSetting.edge,
      padding: '5 10',
      align: titleSetting.align,
      visible: !!showTitle,
      font: { fontSize: titleSetting.font?.fontSize, fontColor: fontC.hex },
      background: {
        bgColor: titleBGC.hex,
        bgAlpha: titleBGC.alpha,
        bgRoundedge: 5,
        borderWidth: 0,
      },
    };
  };

  addMarker = (
    marker: STImageMarker,
    opt: {
      id?: string;
      zOrder?: number;
      dragable?: boolean;
      isActive?: boolean;
      hideTitle?: boolean;
      markerSize?: number;
    } = {}
  ) => {
    const { id: markerId, icon } = marker;
    const { ath, atv, type, mediaIcon, scaleX, scaleY } = icon;
    const id = opt.id || markerId;
    const zOrder = opt.zOrder ?? 0;
    const dragable = !!opt.dragable;

    if (this.player?.getHotspotById(id)) {
      this.removeMarker(id);
    }

    if (type === 1 && mediaIcon) {
      this.player?.addImageHotspot({
        id,
        urls: [
          getImageMarkerUrl({
            src: mediaIcon.url,
            type: mediaIcon.type,
            size: MARKER_IMAGE_SIZE,
          }),
        ],
        showImageBorder: id === EDITING_ID,
        ath: ath,
        atv: atv,
        edge: mediaIcon.anchor,
        scaleX: scaleX,
        scaleY: scaleY,
        zOrder,
        dragable,
        renderer: 'css3d',
      });
    } else {
      this.player?.addShapeHotspot({
        id,
        ath,
        atv,
        zOrder,
        dragable,
        showImageBorder: id === EDITING_ID,
        ...STPlayer.iconParams({
          icon,
          isActive: !!opt.isActive,
          markerSize: opt.markerSize,
        }),
      });
    }

    const textId = spotTitleId(id);
    this.player?.addTextHotspot({
      id: textId,
      ath,
      atv,
      zOrder,
      ...STPlayer.titleParams(marker),
      ...(opt.hideTitle && { visible: false }),
    });
    this.player?.bindDragableHotspot({
      dragableHotspotID: id,
      followingHotspotID: textId,
    });
    if (id !== EDITING_ID) {
      this.player?.getHotspotById(id).addExtendData('onClickId', id);
      this.player?.getHotspotById(textId).addExtendData('onClickId', id);
    }
  };

  removeMarker = (id: string) => {
    this.player?.removeHotspotById(id);
    this.player?.removeHotspotById(spotTitleId(id));
  };

  reOrderMarkers = (markers: STImageMarker[]) => {
    markers.forEach((x, i) => {
      this.player?.updateHotspot({ id: x.id, zOrder: i });
      this.player?.updateTextHotspot({ id: spotTitleId(x.id), zOrder: i });
    });
  };

  private addRadar = () => {
    const editing = this.player?.getHotspotById(EDITING_ID);
    if (!editing) return;
    const { ath, atv } = editing;
    this.player?.addRadarHotspot({
      id: RADAR_ID,
      ath,
      atv,
      zOrder: EDITING_ZORDER - 1,
      fov: 90,
      fillColor: '#F36264',
      fillAlpha: 0.5,
      lineColor: '#ffffff',
      lineAlpha: 1,
      lineWidth: 1,
      radius: (this.radarSize ?? 60) * this.editingScale,
      onOffsetChanged: (angle) => {
        this.onRadarRotate?.(angle - this.northAngle);
      },
    });
    this.player?.bindDragableHotspot({
      dragableHotspotID: EDITING_ID,
      followingHotspotID: RADAR_ID,
    });
  };

  private removeRadar = () => {
    this.player?.removeHotspotById(RADAR_ID);
    this.player?.unbindDragableHotspot({
      dragableHotspotID: EDITING_ID,
      followingHotspotID: RADAR_ID,
    });
  };

  startEditing = (marker: STImageMarker) => {
    const { id } = marker;
    this.editingId = id;
    this.editingScale = marker.icon.scaleX;
    if (this.player?.getHotspotById(id)) {
      this.player?.updateHotspot({
        id,
        visible: false,
      });
      this.player?.updateHotspot({
        id: spotTitleId(id),
        visible: false,
      });
    }
    this.addMarker(marker, {
      id: EDITING_ID,
      zOrder: EDITING_ZORDER,
      dragable: true,
    });
    if (marker.icon.showRadar && marker.data.sceneType === SceneType.pano) {
      this.addRadar();
    }
  };

  quitEditing = () => {
    const editingSpot = this.player?.getHotspotById(EDITING_ID);
    const ath = editingSpot?.ath ?? 0;
    const atv = editingSpot?.atv ?? 0;
    this.removeMarker(EDITING_ID);
    this.removeRadar();
    this.editingScale = 1;
    if (this.editingId && this.player?.getHotspotById(this.editingId)) {
      this.player?.updateHotspot({
        id: this.editingId,
        visible: true,
        ath,
        atv,
      });
      this.player?.updateHotspot({
        id: spotTitleId(this.editingId),
        visible: true,
        ath,
        atv,
      });
      this.onMarkerMoveEnd?.({ id: this.editingId, ath, atv });
    }
  };

  getEditingPos = () => {
    const editingSpot = this.player?.getHotspotById(EDITING_ID);
    return {
      ath: editingSpot?.ath ?? 0,
      atv: editingSpot?.atv ?? 0,
    };
  };

  updateIcon = ({
    icon,
    isActive,
  }: {
    icon: STImageMarkerScene['icon'];
    isActive?: boolean;
  }) => {
    if (icon.type === 1 && icon.mediaIcon) {
      this.player?.updateImageHotspot({
        id: EDITING_ID,
        urls: [
          getImageMarkerUrl({
            src: icon.mediaIcon.url,
            type: icon.mediaIcon.type,
            size: MARKER_IMAGE_SIZE,
          }),
        ],
        edge: icon.mediaIcon.anchor,
        scaleX: icon.scaleX,
        scaleY: icon.scaleY,
        renderer: 'css3d',
      });
    } else {
      this.player?.updateShapeHotspot({
        id: EDITING_ID,
        ...STPlayer.iconParams({ icon, isActive }),
      });
    }
  };

  updateTitle = (marker: STImageMarker) => {
    this.player?.updateTextHotspot({
      id: spotTitleId(EDITING_ID),
      ...STPlayer.titleParams(marker),
    });
  };

  toggleRadar = (isOn: boolean) => {
    if (isOn) {
      this.addRadar();
    } else {
      this.removeRadar();
    }
  };

  updateAngle = (angle: number) => {
    this.player?.updateRadarHotspot({
      id: RADAR_ID,
      offset: angle + this.northAngle,
    });
  };

  updateRadarScale = (scale: number) => {
    this.editingScale = scale;
    if (this.player?.getHotspotById(RADAR_ID)) {
      this.player?.updateRadarHotspot({
        id: RADAR_ID,
        radius: (this.radarSize ?? 60) * scale,
      });
    }
  };

  lookTo = (id: string) => {
    const spot = this.player?.getHotspotById(id);
    if (!spot) return;
    this.player?.lookTo({
      hlookat: spot.ath ?? 0,
      vlookat: spot.atv ?? 0,
    });
  };

  showRadarOnMarker = (id: string) => {
    const spot = this.player?.getHotspotById(id);
    if (!spot) return;
    const { ath, atv } = spot;
    this.player?.addRadarHotspot({
      id: RADAR_ID,
      ath,
      atv,
      zOrder: EDITING_ZORDER - 1,
      fov: viewDefault.fov,
      fillColor: '#F36264',
      fillAlpha: 0.5,
      lineColor: '#ffffff',
      lineAlpha: 1,
      lineWidth: 1,
      radius: this.radarSize ?? 60,
      onOffsetChanged: (angle) => {
        this.onRadarRotate?.(angle - this.northAngle);
      },
    });
  };

  setNorthAngle = (angle: number) => {
    this.northAngle = angle;
  };

  updateRadarFov = (fov?: number) => {
    if (fov) {
      this.player?.updateRadarHotspot({ id: RADAR_ID, fov });
    }
  };

  getPlayer = () => {
    if (!this.player) throw new Error('player is not created');
    return this.player;
  };
}

export const stEditorKrpPlayer = new STPlayer();
export const stSceneKrpPlayer = new STPlayer();
export const stEnterKrpPlayer = new STPlayer();
